import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const services = {
  'celebrity-management': {
    title: 'Celebrity Management',
    tagline: 'Strategic Representation for Elite Talent',
    image: 'https://i.ibb.co/ynkG9FC4/celebrity.jpg', 
    description:
      'We provide comprehensive management solutions for artists, influencers, and public figures. Our approach combines strategic career development with meticulous day-to-day management, ensuring our clients can focus entirely on their craft while we handle the business.',
    features: [
      'Contract Negotiation & Deal Structuring',
      'Brand Partnerships & Endorsement Deals',
      'Tour & Event Logistics Coordination',
      'Media Relations & PR Strategy',
      'Social Media & Digital Presence Management',
      'Financial Planning & Revenue Optimization',
      'Legal Support & Intellectual Property Protection',
      'Career Strategy & Long-term Development'
    ],
    approach: 'Our celebrity management philosophy centers on building sustainable, long-term careers rather than short-term gains. We work closely with each client to understand their vision, values, and goals, then craft a bespoke strategy that aligns opportunities with their authentic brand identity.',
    industries: ['Music', 'Film & Television', 'Sports', 'Digital Content Creation', 'Fashion & Lifestyle']
  },
  'event-management': {
    title: 'Event Management',
    tagline: 'Flawless Execution of Unforgettable Experiences',
    image: 'https://i.ibb.co/qM6286kd/event.jpg', 
    description:
      'From intimate corporate gatherings to large-scale festivals, we orchestrate events that leave lasting impressions. Our end-to-end planning and execution services transform concepts into reality with precision, creativity, and attention to every detail.',
    features: [
      'Concept Development & Creative Direction',
      'Venue Sourcing & Negotiation',
      'Budget Planning & Financial Management',
      'Vendor Coordination & Management',
      'Guest Experience Design',
      'Technical Production & AV Solutions',
      'On-site Event Coordination',
      'Post-Event Analysis & Reporting'
    ],
    approach: 'We believe every event tells a story. Our team works collaboratively with clients to understand the narrative they want to create, then builds an immersive experience that engages attendees from arrival to departure. Our project management methodology ensures nothing is left to chance.',
    industries: ['Corporate Events', 'Product Launches', 'Music Festivals', 'Award Ceremonies', 'Conferences & Summits']
  },
  'club-events': {
    title: 'Club Events',
    tagline: 'Defining the Pulse of Nightlife',
    image: 'https://i.ibb.co/PGhmwYzn/club.jpg', 
    description:
      'We curate high-energy nightlife experiences that set trends and create buzz. From exclusive DJ bookings to thematic production design, our club events deliver unforgettable nights that keep crowds coming back.',
    features: [
      'International DJ & Artist Bookings',
      'Thematic Event Conceptualization',
      'Custom Lighting & Visual Design',
      'Sound System Optimization',
      'VIP & Bottle Service Coordination',
      'Security & Crowd Management',
      'Social Media Marketing & Promotion',
      'Residency Program Development'
    ],
    approach: 'Nightlife is about energy, atmosphere, and moments. We combine data-driven insights about audience preferences with creative innovation to design club experiences that feel fresh yet familiar. Our extensive network of talent and venues allows us to deliver consistently exceptional events.',
    industries: ['Nightclubs', 'Lounge Venues', 'Festival After-parties', 'Pop-up Events', 'Branded Experiences']
  },
  'private-events': {
    title: 'Private Events',
    tagline: 'Bespoke Luxury Experiences',
    image: 'https://i.ibb.co/h1RqPC4V/private.jpg', 
    description:
      'For clients seeking exclusivity and discretion, we craft intimate gatherings that reflect personal style and sophistication. Each private event is a unique creation, designed and executed with absolute confidentiality and meticulous attention to detail.',
    features: [
      'Personalized Event Design & Curation',
      'Private Venue Access & Exclusive Locations',
      'Celebrity & Artist Private Bookings',
      'Gourmet Catering & Beverage Programs',
      'Luxury Transportation & Logistics',
      'Concierge-level Guest Services',
      'Complete Confidentiality & NDAs',
      'Custom Entertainment & Activations'
    ],
    approach: 'Privacy and personalization are paramount. We work directly with clients to understand their vision, preferences, and expectations, then assemble a dedicated team to bring that vision to life. Every detail is tailored, every element is considered, and every moment is crafted for perfection.',
    industries: ['Private Celebrations', 'Corporate Hospitality', 'Milestone Events', 'Intimate Weddings', 'Luxury Retreats']
  },
  'production': {
    title: 'Production',
    tagline: 'Technical Excellence for Perfect Performances',
    image: 'https://i.ibb.co/XxVCPRMD/production.jpg', 
    description:
      'We provide full-scale technical production services that create the perfect canvas for any performance or presentation. Our expertise in sound, lighting, staging, and visual design ensures flawless execution from load-in to load-out.',
    features: [
      'Audio Engineering & Sound Design',
      'Lighting Design & Programming',
      'Stage Design & Construction',
      'LED Screens & Visual Content',
      'Rigging & Safety Management',
      'Power Distribution & Backup Systems',
      'Live Streaming & Recording',
      'Technical Rehearsals & Show Calling'
    ],
    approach: 'Production is where art meets engineering. Our technical team combines creative vision with technical precision to design production solutions that enhance the performance while remaining invisible to the audience. We stay current with the latest technology and techniques to deliver cutting-edge experiences.',
    industries: ['Concerts & Tours', 'Corporate Presentations', 'Theater Productions', 'Festivals', 'Broadcast Events']
  },
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services[slug];
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  // Scroll to top when slug changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white px-6">
        <div className="text-center">
          <h1 className="text-4xl font-light mb-4">Service Not Found</h1>
          <Link to="/" className="text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2">
            <ArrowLeft size={18} />
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-black text-white">
        {/* Hero Section with Image */}
        <section className="relative h-[60vh] md:h-[70vh] border-b border-zinc-800">
          <div className="absolute inset-0">
            <img 
              src={service.image} 
              alt={service.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
          </div>
          
          <div className="relative z-10 h-full flex flex-col justify-end px-6 pb-16">
            <div className="max-w-5xl mx-auto w-full">
              <Link 
                to="/#services" 
                className="text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2 mb-8 text-sm uppercase tracking-wider"
              >
                <ArrowLeft size={16} />
                Back to Services
              </Link>
              
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tight mb-6 leading-none">
                {service.title}
              </h1>
              <p className="text-xl md:text-2xl text-zinc-300 font-light tracking-wide max-w-3xl">
                {service.tagline}
              </p>
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="px-6 py-16 md:py-24 border-b border-zinc-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-6">Overview</h2>
            <p className="text-lg md:text-xl text-zinc-300 leading-relaxed font-light">
              {service.description}
            </p>
          </div>
        </section>

        {/* What We Offer Section */}
        <section className="px-6 py-16 md:py-24 border-b border-zinc-800 bg-zinc-900/20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-12">What We Offer</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {service.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <CheckCircle2 
                    size={20} 
                    className="text-zinc-600 group-hover:text-white transition-colors mt-1 flex-shrink-0" 
                    strokeWidth={1.5}
                  />
                  <span className="text-zinc-300 group-hover:text-white transition-colors font-light">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Approach Section */}
        <section className="px-6 py-16 md:py-24 border-b border-zinc-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-6">Our Approach</h2>
            <p className="text-lg text-zinc-300 leading-relaxed font-light">
              {service.approach}
            </p>
          </div>
        </section>

        {/* Industries Section */}
        <section className="px-6 py-16 md:py-24 border-b border-zinc-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-8">Industries We Serve</h2>
            <div className="flex flex-wrap gap-3">
              {service.industries.map((industry, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 border border-zinc-800 bg-zinc-900/30 text-zinc-400 text-sm uppercase tracking-wider hover:bg-zinc-800/50 hover:text-white transition-all"
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-24 bg-zinc-900/30">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6 uppercase tracking-tight">
              Ready to Work Together?
            </h2>
            <p className="text-zinc-400 mb-8 text-lg font-light">
              Let's discuss how we can bring your vision to life.
            </p>
            <button 
              onClick={() => setIsContactFormOpen(true)}
              className="inline-block px-8 py-4 border border-white text-white uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-all duration-300"
            >
              Get in Touch
            </button>
          </div>
        </section>
      </div>

      {/* Contact Form Modal */}
      <ContactForm 
        isOpen={isContactFormOpen} 
        onClose={() => setIsContactFormOpen(false)}
        selectedService={service.title}
      />
    </>
  );
};

export default ServiceDetail;